import { useState, useRef, useEffect, useCallback } from "react";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config.json";

const TAG_CATEGORIES = [
  { key: "scope",    label: "Scope" },
  { key: "module",   label: "Module" },
  { key: "emission", label: "Emission" },
  { key: "energy",   label: "Energy" },
];

/* ─────────────────────────────────────────────────────────────────────────────
   SearchableInput — text input with filtered suggestion dropdown
───────────────────────────────────────────────────────────────────────────── */
const SearchableInput = ({
  placeholder,
  value,
  onChange,
  suggestions = [],
  onSelect,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filtered = value.trim()
    ? suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
    : suggestions;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", flex: 1 }}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={{
          width: "100%",
          padding: "6px 10px",
          fontSize: 12,
          border: "1px solid #dee2e6",
          borderRadius: 4,
          outline: "none",
          background: disabled ? "#f8f9fa" : "#fff",
          color: disabled ? "#adb5bd" : "#212529",
          boxSizing: "border-box",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {open && !disabled && filtered.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: 4,
            zIndex: 1100,
            maxHeight: 160,
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {filtered.map((s) => (
            <div
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(s);
                setOpen(false);
              }}
              style={{
                padding: "7px 10px",
                fontSize: 12,
                cursor: "pointer",
                borderBottom: "1px solid #f1f3f5",
                color: "#212529",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f7ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   KeywordsManager
───────────────────────────────────────────────────────────────────────────── */
const KeywordsManager = ({ tags, index, handleChange }) => {
  const [allKeywords, setAllKeywords] = useState([]);
  const [loadingKw, setLoadingKw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Per root key draft
  const [drafts, setDrafts] = useState({});
  const [newRootKey, setNewRootKey] = useState("");

  const tagsObj = typeof tags === "object" && tags !== null && !Array.isArray(tags) ? tags : {};

  /* ── fetch keywords ─────────────────────────────────────────────────────── */
  const loadKeywords = useCallback(async () => {
    setLoadingKw(true);
    try {
      const res = await apiCall(
        `${config.ADMIN_API_URL}getKeywords`,
        {},
        {},
        "GET"
      );
      if (res && res.isSuccess) {
        const raw = res.data;
        const data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setAllKeywords(data);
      }
    } catch (e) {
      console.error("loadKeywords error", e);
    } finally {
      setLoadingKw(false);
    }
  }, []);

  useEffect(() => {
    loadKeywords();
  }, [loadKeywords]);

  /* ── grouped keywords map ───────────────────────────────────────────────── */
  const groupedKeywords = allKeywords.reduce((acc, kw) => {
    if (!acc[kw.keyword_type]) acc[kw.keyword_type] = [];
    if (!acc[kw.keyword_type].includes(kw.keyword_value))
      acc[kw.keyword_type].push(kw.keyword_value);
    return acc;
  }, {});

  const allKeyTypes = Object.keys(groupedKeywords).sort();
  const valuesForKey = (key) => (groupedKeywords[key] || []).sort();

  /* ── parent updater ─────────────────────────────────────────────────────── */
  const updateParent = (newTagsObj) => {
    handleChange({ target: { name: "tags", value: newTagsObj } }, index);
  };

  /* ── root key operations ────────────────────────────────────────────────── */
  const addRootKey = () => {
    const k = newRootKey.trim();
    if (k && !tagsObj[k]) {
      updateParent({ ...tagsObj, [k]: [] });
      setNewRootKey("");
    }
  };

  const removeRootKey = (rootKey) => {
    const newTags = { ...tagsObj };
    delete newTags[rootKey];
    updateParent(newTags);
  };

  /* ── tag operations ─────────────────────────────────────────────────────── */
  const setTag = (rootKey, key, value) => {
    const newTags = { ...tagsObj };
    if (!newTags[rootKey]) newTags[rootKey] = [];

    // Check if exactly the same tag exists
    const existingIdx = newTags[rootKey].findIndex((t) => t.key === key && t.value === value);
    if (existingIdx === -1) {
      newTags[rootKey].push({ key, value });
    }
    updateParent(newTags);
  };

  const removeTag = (rootKey, key, value) => {
    const newTags = { ...tagsObj };
    if (newTags[rootKey]) {
      newTags[rootKey] = newTags[rootKey].filter((t) => t.key !== key || t.value !== value);
      updateParent(newTags);
    }
  };

  /* ── create new keyword ─────────────────────────────────────────────────── */
  const handleCreateNew = async (rootKey) => {
    const draft = drafts[rootKey] || { key: "", value: "" };
    const { key: k, value: v } = draft;
    if (!k.trim() || !v.trim()) {
      setError("Both key and value are required.");
      return;
    }
    setSaving(true);
    try {
      const res = await apiCall(
        `${config.ADMIN_API_URL}createKeyword`,
        {},
        { keyword_type: k.trim(), keyword_value: v.trim() },
        "POST"
      );
      if (res && res.isSuccess) {
        await loadKeywords();
        setTag(rootKey, k.trim(), v.trim());
        updateDraft(rootKey, "key", "");
        updateDraft(rootKey, "value", "");
        setError(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const updateDraft = (rootKey, field, val) => {
    setDrafts((prev) => ({
      ...prev,
      [rootKey]: { ...(prev[rootKey] || { key: "", value: "" }), [field]: val }
    }));
  };

  /* ── render ────────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 8,
        background: "#f8f9fa",
        padding: 12,
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#495057",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {loadingKw ? "Loading keywords…" : "Tags Configuration"}
        </span>
      </div>

      {Object.entries(tagsObj).map(([rootKey, tagsArr]) => {
        const draft = drafts[rootKey] || { key: "", value: "" };
        const draftValueSuggestions = valuesForKey(draft.key);

        return (
          <div
            key={rootKey}
            style={{
              marginBottom: 12,
              padding: 10,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 6,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            {/* Category Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0d6efd" }}>
                {TAG_CATEGORIES.find(c => c.key === rootKey)?.label ?? rootKey}
              </span>
              <button
                type="button"
                onClick={() => removeRootKey(rootKey)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#dc3545",
                  fontSize: 10,
                  cursor: "pointer",
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                Remove Category
              </button>
            </div>

            {/* Selected Tags for this Category */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
              {tagsArr.length === 0 ? (
                <span style={{ fontSize: 11, color: "#adb5bd", fontStyle: "italic" }}>
                  No tags added under {rootKey}.
                </span>
              ) : (
                tagsArr.map((tag, tIdx) => (
                  <span
                    key={`${tag.key}-${tag.value}-${tIdx}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: "#e7f1ff",
                      border: "1px solid #cfe2ff",
                      borderRadius: 4,
                      fontSize: 11,
                      color: "#084298",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{tag.key}:</span>
                    <span>{tag.value}</span>
                    <span
                      onClick={() => removeTag(rootKey, tag.key, tag.value)}
                      style={{
                        cursor: "pointer",
                        marginLeft: 2,
                        fontWeight: "bold",
                        color: "#6ea8fe",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                  </span>
                ))
              )}
            </div>

            {/* Tag Selector for this Category */}
            <div style={{ background: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: 6, padding: 10 }}>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Key
                </label>
                <SearchableInput
                  placeholder="Type or select a key…"
                  value={draft.key}
                  onChange={(v) => updateDraft(rootKey, "key", v)}
                  suggestions={allKeyTypes}
                  onSelect={(v) => updateDraft(rootKey, "key", v)}
                />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: draft.key.trim() ? "#6c757d" : "#adb5bd", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Value {!draft.key.trim() && <span style={{ fontWeight: 400, textTransform: "none" }}>(select a key first)</span>}
                </label>
                <SearchableInput
                  placeholder={draft.key.trim() ? "Type or select a value…" : "Select a key first"}
                  value={draft.value}
                  onChange={(v) => updateDraft(rootKey, "value", v)}
                  suggestions={draftValueSuggestions}
                  onSelect={(v) => updateDraft(rootKey, "value", v)}
                  disabled={!draft.key.trim()}
                />
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    if (!draft.key.trim() || !draft.value.trim()) return;
                    setTag(rootKey, draft.key.trim(), draft.value.trim());
                    updateDraft(rootKey, "key", "");
                    updateDraft(rootKey, "value", "");
                    setError(null);
                  }}
                  disabled={!draft.key.trim() || !draft.value.trim()}
                  style={{
                    flex: 1, padding: "6px 0", fontSize: 11, fontWeight: 600,
                    background: draft.key.trim() && draft.value.trim() ? "#0d6efd" : "#e9ecef",
                    color: draft.key.trim() && draft.value.trim() ? "#fff" : "#adb5bd",
                    border: "none", borderRadius: 4, cursor: draft.key.trim() && draft.value.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  Add Tag
                </button>

                <button
                  type="button"
                  onClick={() => handleCreateNew(rootKey)}
                  disabled={saving || !draft.key.trim() || !draft.value.trim()}
                  style={{
                    flex: 1, padding: "6px 0", fontSize: 11, fontWeight: 600,
                    background: !saving && draft.key.trim() && draft.value.trim() ? "#198754" : "#e9ecef",
                    color: !saving && draft.key.trim() && draft.value.trim() ? "#fff" : "#adb5bd",
                    border: "none", borderRadius: 4, cursor: saving || !draft.key.trim() || !draft.value.trim() ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : "+ Create New Key-Value Pair"}
                </button>
              </div>
              {error && <div style={{ color: "#dc3545", fontSize: 10, marginTop: 6 }}>{error}</div>}
            </div>
          </div>
        );
      })}

      {/* Add Category — fixed dropdown */}
      {(() => {
        const available = TAG_CATEGORIES.filter(c => !tagsObj[c.key]);
        if (available.length === 0) return null;
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
            <select
              value={newRootKey}
              onChange={(e) => setNewRootKey(e.target.value)}
              style={{
                flex: 1,
                padding: "8px 12px",
                fontSize: 13,
                border: "1px solid #ced4da",
                borderRadius: 6,
                outline: "none",
                background: "#fff",
                color: newRootKey ? "#212529" : "#6c757d",
                cursor: "pointer",
              }}
            >
              <option value="">— Select a tag category —</option>
              {available.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addRootKey}
              disabled={!newRootKey}
              style={{
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
                background: newRootKey ? "#0d6efd" : "#e9ecef",
                color: newRootKey ? "#fff" : "#adb5bd",
                border: "none",
                borderRadius: 6,
                cursor: newRootKey ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
              }}
            >
              + Add
            </button>
          </div>
        );
      })()}

    </div>
  );
};

export default KeywordsManager;